import React from 'react';
import type { KeyValueRow } from '../../services/api/paymentApi';
import { ADMIN_COLORS } from '../../theme';

interface PaymentOrderSettingsProps {
  config: Record<string, any>;
  isPlanMode: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

const TOKEN_HELP =
  'Tokens: {formId} {leadId} {fieldName} {provider} {planId} {query.name}. Filled in on the server when the order is created.';

const KeyValueEditor: React.FC<{
  label: string;
  rows: KeyValueRow[];
  onChange: (rows: KeyValueRow[]) => void;
}> = ({ label, rows, onChange }) => (
  <div className="form-group">
    <label className="control-label">{label}</label>
    {rows.map((row, index) => (
      <div key={index} className="d-flex mb-2" style={{ gap: 8 }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="key"
          value={row.key}
          onChange={(e) => onChange(rows.map((r, i) => (i === index ? { ...r, key: e.target.value } : r)))}
        />
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="value"
          value={row.value}
          onChange={(e) => onChange(rows.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)))}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          aria-label={`Remove ${label} row ${index + 1}`}
          onClick={() => onChange(rows.filter((_, i) => i !== index))}
        >
          ×
        </button>
      </div>
    ))}
    <button
      type="button"
      className="rfb-add-btn"
      onClick={() => onChange([...rows, { key: '', value: '' }])}
    >
      <span aria-hidden>+</span> Add row
    </button>
  </div>
);

/** Currency, GST rate and the extra fields sent with every create-order request. */
const PaymentOrderSettings: React.FC<PaymentOrderSettingsProps> = ({ config, isPlanMode, onChange }) => (
  <div
    style={{
      border: `1px solid ${ADMIN_COLORS.border}`,
      borderRadius: 12,
      padding: 12,
      marginBottom: 14,
      background: ADMIN_COLORS.headBg,
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 8 }}>Pricing &amp; order settings</div>
    <div className="d-flex" style={{ gap: 12 }}>
      <div className="form-group" style={{ flex: 1 }}>
        <label className="control-label" htmlFor="paymentGstRate">GST rate (%)</label>
        <input
          id="paymentGstRate"
          type="number"
          min={0}
          max={100}
          className="form-control form-control-sm"
          placeholder="18"
          value={config.gstRate ?? ''}
          onChange={(e) => onChange({ gstRate: e.target.value })}
        />
      </div>
    </div>
    <div className="form-group">
      <label className="control-label" htmlFor="paymentOrderEntity">Order entity</label>
      <input
        id="paymentOrderEntity"
        type="text"
        className="form-control form-control-sm"
        placeholder="form-builder"
        value={config.orderEntity ?? ''}
        onChange={(e) => onChange({ orderEntity: e.target.value })}
      />
    </div>
    <div className="d-flex" style={{ gap: 12 }}>
      <div className="form-group" style={{ flex: 1 }}>
        <label className="control-label" htmlFor="paymentOrderReference">Reference ID</label>
        <input
          id="paymentOrderReference"
          type="text"
          className="form-control form-control-sm"
          placeholder="{formId}"
          value={config.orderReferenceId ?? ''}
          onChange={(e) => onChange({ orderReferenceId: e.target.value })}
        />
      </div>
      <div className="form-group" style={{ flex: 1 }}>
        <label className="control-label" htmlFor="paymentOrderIdentifier">Identifier</label>
        <input
          id="paymentOrderIdentifier"
          type="text"
          className="form-control form-control-sm"
          placeholder="{formId}"
          value={config.orderIdentifier ?? ''}
          onChange={(e) => onChange({ orderIdentifier: e.target.value })}
        />
      </div>
    </div>
    <small className="form-text text-muted mb-3">
      {TOKEN_HELP} The Description field above supports tokens too.
    </small>
    {isPlanMode && (
      <div className="custom-control custom-checkbox mb-3">
        <input
          id="paymentNotify"
          className="custom-control-input"
          type="checkbox"
          checked={config.notify !== false}
          onChange={(e) => onChange({ notify: e.target.checked })}
        />
        <label className="custom-control-label" htmlFor="paymentNotify">
          Let the gateway notify the customer about the subscription
        </label>
      </div>
    )}
    <KeyValueEditor
      label="Order notes"
      rows={config.orderNotes || []}
      onChange={(orderNotes) => onChange({ orderNotes })}
    />
    <div className="custom-control custom-checkbox mb-2">
      <input
        id="paymentInvoiceEnabled"
        className="custom-control-input"
        type="checkbox"
        checked={Boolean(config.invoiceEnabled)}
        onChange={(e) => onChange({ invoiceEnabled: e.target.checked })}
      />
      <label className="custom-control-label" htmlFor="paymentInvoiceEnabled">
        Send invoice details (item, tax, discount, total) with the order
      </label>
    </div>
    {config.invoiceEnabled && (
      <div className="form-group">
        <label className="control-label" htmlFor="paymentInvoiceItem">Invoice item description</label>
        <input
          id="paymentInvoiceItem"
          type="text"
          className="form-control form-control-sm"
          placeholder="Defaults to the Description above"
          value={config.invoiceItemDescription ?? ''}
          onChange={(e) => onChange({ invoiceItemDescription: e.target.value })}
        />
      </div>
    )}
    <KeyValueEditor
      label="Additional data"
      rows={config.additionalData || []}
      onChange={(additionalData) => onChange({ additionalData })}
    />
    <KeyValueEditor
      label="Split tags (revenue split)"
      rows={config.splitTags || []}
      onChange={(splitTags) => onChange({ splitTags })}
    />
  </div>
);

export default PaymentOrderSettings;
